// ---------------------------------------------------------------------------
// EST-CE QU'IL SORT VRAIMENT DU SON ? — la question que personne ne posait
//
// Toji, 14/09 : « je crois que j'avais des problèmes de son aussi. Faut que tu
// t'assures que toute la gestion du son, elle est parfaite, parce que je crois
// que j'avais testé justement un fichier et on n'avait aucun son. »
//
// ⚠️ L'ESCALIER NE DESCEND QUE SUR UNE ERREUR — ET CELLE-CI N'EN LÈVE AUCUNE.
//
// Les trois crans (`direct`, `mse`, `remux`) s'enchaînent quand la balise
// REFUSE le flux : `MEDIA_ERR_SRC_NOT_SUPPORTED`, la raison `direct-refuse`.
// Or un .mp4 dont seule la piste audio est illisible — E-AC-3, DTS, AC-3, que
// Chrome refuse pour des raisons de licence, voir `codecs-video.js` — n'est pas
// refusé du tout : Chrome démuxe le fichier, joue l'image, ignore la piste
// qu'il ne décode pas, et ne dit RIEN. `error` reste nul, `readyState` monte à
// 4, le temps avance, la lecture est parfaite. Il n'y a simplement aucun son.
//
// Aucun des trois crans ne pouvait le voir : `codecs-video.js` juge le
// démuxeur MSE, pas la balise en relais direct, et c'est justement le relais
// direct qui sert les .mp4 tels quels.
//
// ⚠️ ET CE N'EST PAS UNE ABSENCE DE SON QU'IL FAUT CHERCHER, C'EST UN COMPTEUR.
//
// Déjà payé le 12/09, en mesurant libmedia : un événement « son rendu » ment
// quand les compteurs disent zéro. La seule chose qui ne mente pas est le
// nombre d'octets réellement DÉCODÉS —
// `webkitAudioDecodedByteCount`, que Chrome expose (et lui seul, avec Edge).
// Zéro octet audio décodé pendant que la vidéo en décode des millions, c'est
// un silence, quoi que dise le reste.
//
// Ce module est la RÈGLE, sans DOM : elle se tient dans un banc, ce qui est la
// seule façon d'éprouver les quatre cas où il ne faut RIEN conclure.
// ---------------------------------------------------------------------------

/** Le média doit avoir vraiment joué avant qu'un compteur à zéro veuille dire quelque chose. */
export const SECONDES_AVANT_DE_CONCLURE = 3;

/** …et la vidéo doit décoder, sinon c'est la lecture entière qui est à l'arrêt. */
export const OCTETS_VIDEO_MINIMUM = 64 * 1024;

/** Au-delà, le guetteur abandonne : un verdict qui ne vient pas ne viendra pas. */
export const TOURS_AU_PLUS = 30;

/**
 * Y a-t-il du son ?
 *
 * @param {object} vu ce qu'on observe de la balise :
 *   `jouees` (secondes de média réellement écoulées depuis la pose),
 *   `octetsAudio` / `octetsVideo` (les compteurs, ou `null` s'ils n'existent
 *   pas sur ce navigateur), `pisteAnnoncee` (le fichier déclare-t-il une piste
 *   audio ? — la sonde le sait, via `/api/audio`).
 * @returns {{verdict: 'muet'|'sonore'|'inconnu', pourquoi: string}}
 *
 * ⚠️ « inconnu » N'EST PAS UN ÉCHEC, et c'est la moitié qui compte. Descendre
 * l'escalier sur une absence d'information reviendrait à remuxer tout Firefox
 * et tout Safari — qui n'exposent pas ces compteurs — pour un défaut qu'ils
 * n'ont peut-être pas. On ne descend que sur une CERTITUDE.
 */
export function sonSorti({
  jouees = 0, octetsAudio = null, octetsVideo = null, pisteAnnoncee = true,
} = {}) {
  // 1. Les compteurs n'existent pas : ce navigateur ne répond pas à la
  //    question. Firefox, Safari. On ne conclut rien, jamais.
  if (!Number.isFinite(octetsAudio) || !Number.isFinite(octetsVideo)) {
    return { verdict: 'inconnu', pourquoi: 'ce navigateur ne compte pas les octets décodés' };
  }
  // 2. Le fichier n'a pas de piste audio du tout. Un documentaire muet, un
  //    extrait sans bande-son : le silence est le fichier, pas nous. Remuxer
  //    n'y changerait rien et coûterait une conversion.
  if (!pisteAnnoncee) {
    return { verdict: 'inconnu', pourquoi: 'le fichier ne déclare aucune piste audio' };
  }
  // 3. Trop tôt. Le compteur audio démarre au premier paquet décodé, qui peut
  //    suivre la première image de quelques centaines de millisecondes. Juger
  //    à la pose ferait descendre l'escalier sur CHAQUE lecture.
  if (jouees < SECONDES_AVANT_DE_CONCLURE) {
    return { verdict: 'inconnu', pourquoi: `seulement ${jouees.toFixed(1)} s jouées` };
  }
  // 4. La vidéo elle-même ne décode pas : ce n'est pas un problème de son,
  //    c'est une lecture à l'arrêt, et elle a ses propres filets. Conclure ici
  //    accuserait le son d'une panne qui n'est pas la sienne.
  if (octetsVideo < OCTETS_VIDEO_MINIMUM) {
    return { verdict: 'inconnu', pourquoi: `la vidéo ne décode pas non plus (${octetsVideo} o)` };
  }
  // Le seul cas certain : la vidéo décode, le temps passe, le fichier annonce
  // du son, et pas un octet audio n'a été décodé.
  if (octetsAudio === 0) {
    return {
      verdict: 'muet',
      pourquoi: `${jouees.toFixed(1)} s jouées, ${octetsVideo} o de vidéo décodés, 0 o de son`,
    };
  }
  return { verdict: 'sonore', pourquoi: `${octetsAudio} o de son décodés` };
}

/**
 * Le guetteur : il relève les compteurs à intervalle régulier et n'appelle
 * `surMuet` qu'UNE fois, sur le premier verdict certain.
 *
 * ⚠️ UNE SEULE FOIS, ET C'EST STRUCTUREL. Le remède à un verdict « muet » est
 * de descendre l'escalier, ce qui repose la source, ce qui redémarre un
 * guetteur. Sans cette garde, un fichier réellement sans son (mais qui en
 * déclare un — ça existe : piste présente et vide) ferait descendre l'escalier
 * en boucle jusqu'à son dernier cran, en brûlant une conversion à chaque tour.
 *
 * Purement fonctionnel : `relever` rend ce qu'on observe, `planifier` et
 * `annuler` sont les minuteurs. Le banc les fournit à la main ; `lecteur.js`
 * passe la balise et `setInterval`.
 */
export function guetterLeSon({
  relever, surMuet, planifier, annuler, pasMs = 1000, tours = TOURS_AU_PLUS,
} = {}) {
  let minuteur = null;
  let fini = false;
  let vus = 0;

  const arreter = () => {
    if (minuteur !== null) { annuler(minuteur); minuteur = null; }
  };

  const tour = () => {
    if (fini) return;
    let vu;
    // ⚠️ RELEVER NE DOIT JAMAIS CASSER UNE LECTURE. Les compteurs sont des
    // propriétés non standard : un jour elles disparaissent, et ce serait la
    // pire façon de perdre une soirée.
    try { vu = relever(); } catch { return; }
    const r = sonSorti(vu);
    // ⚠️ SUR FIREFOX ET SAFARI, LE VERDICT EST « INCONNU » POUR TOUJOURS.
    // Sans ce plafond, l'intervalle d'une lecture survivrait a la fermeture du
    // lecteur et ne s'arreterait qu'a la pose suivante — une minuterie de plus
    // a chaque episode ouvert puis quitte. Trente tours : bien au-dela de ce
    // qu'il faut pour trancher, et borne.
    vus += 1;
    if (r.verdict === 'inconnu') {
      if (vus >= tours) { fini = true; arreter(); }
      return;
    }
    // Un verdict certain, quel qu'il soit, clôt la question pour cette source.
    fini = true;
    arreter();
    if (r.verdict === 'muet') surMuet(r);
  };

  minuteur = planifier(tour, pasMs);
  return { arreter, tour };
}

/**
 * Ce qu'on observe d'une balise `<video>`, prêt pour `sonSorti`.
 *
 * `depuis` est la valeur de `currentTime` au moment où la source a été posée :
 * le remux repart de zéro mais le relais direct, lui, garde la position de la
 * source précédente — compter `currentTime` brut donnerait « 720 s jouées » à
 * la première image et conclurait aussitôt.
 */
export function releverDe(video, { depuis = 0, pisteAnnoncee = true } = {}) {
  const nombre = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    jouees: Math.max(0, (Number(video?.currentTime) || 0) - depuis),
    octetsAudio: nombre(video?.webkitAudioDecodedByteCount),
    octetsVideo: nombre(video?.webkitVideoDecodedByteCount),
    pisteAnnoncee,
  };
}
